/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
*/

import {
    Component, Input, OnDestroy,
    OnInit
} from '@angular/core';
import {SafeHtml} from '@angular/platform-browser';
import {EditorView} from 'prosemirror-view';
import {Subscription} from 'rxjs';
import Editor from '../../../Editor';
import Icon from '../../../icons';
import {TBItems} from '../../../types';
import {SanitizeHtmlPipe} from '../../../pipes/sanitize/sanitize-html.pipe';
import {ToggleCommands} from '../MenuCommands';
import {FcAgEditorService} from '../../../editor.service';

/**
 * Bubble Component.
 */
@Component({
    selector: 'fc-ag-bubble',
    templateUrl: './bubble.component.html',
    styleUrls: ['./bubble.component.scss']
})
export class BubbleComponent implements OnInit, OnDestroy {
    /**
     * Default constructor.
     * 
     * @param sanitizeHTML Sanitize Html Pipe.
     * @param fcAgEditorService FcAgEditorService.
     */
    constructor(private sanitizeHTML: SanitizeHtmlPipe, private fcAgEditorService: FcAgEditorService) {
    }

    private get view(): EditorView {
        return this.editor.view;
    }

    /**
     * Editor.
     */
    @Input() editor: Editor;

    private updateSubscription: Subscription;

    /**
     * Executable Items.
     */
    execulableItems: TBItems[] = [];

    /**
     * Active Items.
     */
    activeItems: TBItems[] = [];

    /**
     * Toolbar.
     */
    toolbar: TBItems[][] = [
        ['bold', 'italic', 'underline', 'strike'],
        ['ordered_list', 'bullet_list', 'blockquote', 'code'],
        ['align_left', 'align_center', 'align_right', 'align_justify']
    ];

    /**
     * Toggle commands.
     */
    toggleCommands: TBItems[] = [
        'bold', 'italic', 'underline', 'strike',
        'ordered_list', 'bullet_list', 'blockquote', 'code',
        'align_left', 'align_center', 'align_right', 'align_justify'
    ];

    /**
     * Get Icon.
     * 
     * @param name Icon name.
     * @returns SafeHtml.
     */
    getIcon(name: TBItems): SafeHtml {
        const icon = Icon.get(name);
        return this.sanitizeHTML.transform(icon);
    }

    /**
     * Get title.
     * 
     * @param name Name.
     * @returns String.
     */
    getTitle(name: string): string {
        return this.fcAgEditorService.locals.get(name);
    }

    /**
     * Track by index.
     * 
     * @param index Index.
     * @returns Number.
     */
    trackByIndex(index: number): number {
        return index;
    }

    /**
     * On click callback.
     * 
     * @param e Mouse Event.
     * @param commandName Command name.
     */
    onClick(e: MouseEvent, commandName: TBItems): void {
        e.preventDefault();
        e.stopPropagation();

        if (e.button !== 0) {
            return;
        }

        const {state, dispatch} = this.view;

        const command = ToggleCommands[commandName];
        command.toggle()(state, dispatch);
    }

    private update(view: EditorView): void {
        this.activeItems = [];
        this.execulableItems = [];
        const {state} = view;

        this.toggleCommands.forEach(toolbarItem => {
            const command = ToggleCommands[toolbarItem];

            const isActive = command.isActive(state);
            if (isActive) {
                this.activeItems.push(toolbarItem);
            }

            const canExecute = command.canExecute(state);

            if (canExecute) {
                this.execulableItems.push(toolbarItem);
            }
        });
    }

    /**
     * Angular life cycle.
     */
    ngOnInit(): void {
        this.updateSubscription = this.editor.update
            .subscribe((view) => {
                this.update(view);
            });
    }

    /**
     * Angular life cycle.
     */
    ngOnDestroy(): void {
        this.updateSubscription.unsubscribe();
    }
}
